// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import transformContentHtml from "@insite/client-framework/Common/Utilities/transformContentHtml";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { IconMemo, IconProps } from "@insite/mobius/Icon";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Tab, { TabPresentationProps, TabProps } from "@insite/mobius/Tab";
import TabGroup, { TabGroupPresentationProps } from "@insite/mobius/TabGroup";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import parse from "html-react-parser";
import * as React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const enum fields {
    showDocuments = "showDocuments",
    showAttributes = "showAttributes",
}

type Props = WidgetProps & HasProduct & ReturnType<typeof mapStateToProps>;

const mapStateToProps = (state: ApplicationState) => {
    return {
        productSettings: getSettingsCollection(state).productSettings,
    };
};

export interface ProductDetailsSpecificationsStyles {
    tabGroup?: TabGroupPresentationProps;
    tabStyles?: TabPresentationProps;
    documentsContainer?: GridContainerProps;
    documentGridItem?: GridItemProps;
    documentLink?: LinkPresentationProps;
    documentIcon?: IconProps;
    documentNameText?: TypographyPresentationProps;
    attributeTypeWrapper?: InjectableCss;
    attributeTypeLabelText?: TypographyPresentationProps;
    attributeTypeValuesText?: TypographyPresentationProps;
    attrGroupsWrapper?: InjectableCss;
    leftAttrGroup?: InjectableCss;
    rightAttrGroup?: InjectableCss;
    printGroup?: InjectableCss;
}

export const specificationsStyles: ProductDetailsSpecificationsStyles = {
    tabGroup: {
        cssOverrides: {
            wrapper: css`
                padding: 0;
                display: block;
                margin-bottom: 70px;
                @media print {
                    display: none;
                }
            `,
            tabGroup: css`
                overflow-y: hidden;
                position: relative;
                padding-bottom: 0;
                padding: 0;
                z-index: 0;
                min-height: 30px;
                border-bottom: 2px solid ${getColor("common.ui3")};

                && .product-tab {
                    margin-right: 60px;
                    &&&:first-child {
                        margin-right: 0;
                    }
                }
            `,
            tabContent: css`
                border: none;
                margin: 0;
                padding: 32px 32px 0;

                @media (max-width: 768px) {
                    padding: 32px 15px 0;
                }
            `,
        },
    },
    tabStyles: {
        mergeCss: true,
        typographyProps: {
            as: "h2",
        },
    },
    documentsContainer: {
        gap: 5,
    },
    documentGridItem: {
        width: [12, 12, 6, 6, 6],
    },
    documentIcon: {
        src: "File",
        color: "text.link",
        css: css`
            margin-right: 10px;
        `,
    },
    documentNameText: {
        css: css`
            vertical-align: super;
        `,
    },
    attributeTypeLabelText: {
        weight: "bold",
        css: css`
            display: block;
            min-width: 190px;
            text-align: right;
            margin-right: 30px;
            flex: 1;
            @media (max-width: 600px) {
                min-width: auto;
                margin-right: 10px;
            }
        `,
    },
    attributeTypeValuesText: {
        css: css`
            flex: 1;
        `,
    },
    attributeTypeWrapper: {
        css: css`
            padding: 15px 0 10px;
            display: flex;
            border-top: 1px solid ${getColor("common.ui3")};
            &&&:last-child {
                border-bottom: 1px solid ${getColor("common.ui3")};
            }
            page-break-inside: avoid;
        `,
    },
    attrGroupsWrapper: {
        css: css`
            display: flex;
            width: 100%;
            flex-wrap: wrap;
        `,
    },
    leftAttrGroup: {
        css: css`
            width: 50%;
            padding-right: 15px;

            @media (max-width: 768px) {
                width: 100%;
                padding-right: 0;

                &&& .attr-wrapper:last-child {
                    border-bottom: 0;
                }
            }
        `,
    },
    rightAttrGroup: {
        css: css`
            width: 50%;
            padding-left: 15px;

            @media (max-width: 768px) {
                width: 100%;
                padding-left: 0;
            }
        `,
    },
    printGroup: {
        css: css`
            display: none;
            @media print {
                display: block;
                margin-bottom: 20px;
            }
        `,
    },
};

const styles = specificationsStyles;

const ProductDetailsSpecifications: React.FC<Props> = ({
    product: { brand, attributeTypes, documents, specifications, content },
    fields: { showDocuments, showAttributes, documentsPosition, attributesPosition },
    productSettings: { attributesTabSortOrder, documentsTabSortOrder },
}) => {
    const renderingDocuments = showDocuments && documents && documents.length > 0;
    const renderingAttributes =
        showAttributes &&
        attributeTypes &&
        attributeTypes?.filter(attributeType => attributeType.includeOnProduct).length > 0;
    const renderingSpecifications = specifications && specifications.length > 0;
    const documentsFirst = documentsTabSortOrder === "DisplayFirst";
    const attributesFirst = attributesTabSortOrder === "DisplayFirst";
    const renderHtmlContent = content?.htmlContent && content?.htmlContent?.length > 0;

    if (!renderingDocuments && !renderingAttributes && !renderingSpecifications && !renderHtmlContent) {
        return null;
    }

    const specificationsTab: any = [];
    const documentsTab: any = [];
    const attributesTab: any = [];
    const htmlContentTab: any = [];

    if (renderingDocuments) {
        documentsTab.push(
            <Tab
                className="product-tab"
                headline={translate("Resources")}
                key="resources"
                tabKey="resources"
                data-test-selector="productDetails_specifications_resources"
                {...styles.tabStyles}
            >
                <GridContainer {...styles.documentsContainer}>
                    {documents!.map(document => (
                        <GridItem key={document.id.toString()} {...styles.documentGridItem}>
                            <Link
                                href={document.filePath}
                                target="_new"
                                {...styles.documentLink}
                                data-test-selector={`productDetails_${document.id}`}
                            >
                                <IconMemo {...styles.documentIcon} />
                                <Typography {...styles.documentNameText}>{document.name}</Typography>
                            </Link>
                        </GridItem>
                    ))}
                </GridContainer>
            </Tab>,
        );
    }

    if (renderingAttributes) {
        const attributes = [...attributeTypes!].filter(attributeType => attributeType.includeOnProduct);
        const firstHalfAttr = attributes!.splice(0, Math.ceil(attributes!.length / 2));
        const secondHalfAttr = attributes;

        attributesTab.push(
            <Tab
                className="product-tab"
                headline={translate("Attributes")}
                key="attributes"
                tabKey="attributes"
                data-test-selector="productDetails_specifications_attributes"
                {...styles.tabStyles}
            >
                <StyledWrapper {...styles.attrGroupsWrapper}>
                    <StyledWrapper {...styles.leftAttrGroup}>
                        {firstHalfAttr &&
                            firstHalfAttr.map(attr => (
                                <StyledWrapper
                                    {...styles.attributeTypeWrapper}
                                    className="attr-wrapper"
                                    key={attr.id.toString()}
                                >
                                    <Typography {...styles.attributeTypeLabelText}>{attr.label}:</Typography>
                                    <Typography {...styles.attributeTypeValuesText}>
                                        {attr
                                            .attributeValues!.map(attributeValue => attributeValue.valueDisplay)
                                            .join(", ")}
                                    </Typography>
                                </StyledWrapper>
                            ))}
                    </StyledWrapper>
                    <StyledWrapper {...styles.rightAttrGroup}>
                        {secondHalfAttr &&
                            secondHalfAttr.map(attr => (
                                <StyledWrapper {...styles.attributeTypeWrapper} key={attr.id.toString()}>
                                    <Typography {...styles.attributeTypeLabelText}>{attr.label}:</Typography>
                                    <Typography {...styles.attributeTypeValuesText}>
                                        {attr
                                            .attributeValues!.map(attributeValue => attributeValue.valueDisplay)
                                            .join(", ")}
                                    </Typography>
                                </StyledWrapper>
                            ))}
                    </StyledWrapper>
                </StyledWrapper>
            </Tab>,
        );
    }

    if (renderHtmlContent) {
        htmlContentTab.push(
            <Tab
                className="product-tab"
                headline={translate("Product Details")}
                key="productDetails"
                tabKey="productDetails"
                data-test-selector="productDetails_htmlContent"
                {...styles.tabStyles}
            >
                <div data-test-selector="productDetails_htmlContent">
                    {parse(transformContentHtml(content.htmlContent), parserOptions)}
                </div>
            </Tab>,
        );
    }

    if (renderingSpecifications) {
        specificationsTab.push(
            specifications?.map((specification, index) => {
                return (
                    <Tab
                        className="product-tab"
                        key={specification.id.toString()}
                        headline={specification.nameDisplay}
                        tabKey={specification.id.toString()}
                        data-test-selector="productDetails_specifications_specification"
                        {...styles.tabStyles}
                    >
                        {parse(transformContentHtml(specification.htmlContent), parserOptions)}
                    </Tab>
                );
            }),
        );
    }

    let tabs = specificationsTab;
    if (documentsFirst) {
        tabs = [...documentsTab, ...tabs];
    } else {
        tabs = [...tabs, ...documentsTab];
    }

    if (attributesFirst) {
        tabs = [...attributesTab, ...tabs];
    } else {
        tabs = [...tabs, ...attributesTab];
    }
    // Fix fir if both are display first this will be duplicated
    tabs = [...htmlContentTab, ...tabs];

    return (
        <>
            <TabGroup {...styles.tabGroup} data-test-selector="productDetails_specifications">
                {tabs}
            </TabGroup>
            {content?.htmlContent && content?.htmlContent.length > 0 && (
                <StyledWrapper {...styles.printGroup}>
                    <Typography
                        as="h3"
                        variant="h3"
                        css={css`
                            page-break-inside: avoid;
                        `}
                    >
                        {translate("Product Details")}
                    </Typography>
                    <Typography>{parse(transformContentHtml(content?.htmlContent), parserOptions)}</Typography>
                </StyledWrapper>
            )}
            {attributeTypes && attributeTypes.length > 0 && (
                <StyledWrapper {...styles.printGroup}>
                    <Typography
                        as="h3"
                        variant="h3"
                        css={css`
                            page-break-inside: avoid;
                        `}
                    >
                        {translate("Attributes")}
                    </Typography>
                    {attributeTypes &&
                        attributeTypes.map(attributeType => (
                            <StyledWrapper {...styles.attributeTypeWrapper} key={attributeType.id.toString()}>
                                <Typography {...styles.attributeTypeLabelText}>
                                    {attributeType.label || attributeType.name}:&nbsp;
                                </Typography>
                                <Typography {...styles.attributeTypeValuesText}>
                                    {attributeType
                                        .attributeValues!.map(attributeValue => attributeValue.valueDisplay)
                                        .join(", ")}
                                </Typography>
                            </StyledWrapper>
                        ))}
                </StyledWrapper>
            )}
            {specifications?.map((specification, index) => {
                let htmlContent = specification.htmlContent;
                if (htmlContent.includes("<=")) {
                    htmlContent = htmlContent.replace(/<=/g, "&lt=");
                }
                if (htmlContent.includes("=<")) {
                    htmlContent = htmlContent.replace(/=</g, "=&lt");
                }
                if (htmlContent.includes(">=")) {
                    htmlContent = htmlContent.replace(/>=/g, "&gt=");
                }
                if (htmlContent.includes("=>")) {
                    htmlContent = htmlContent.replace(/=>/g, "=&gt");
                }

                return (
                    <StyledWrapper key={`${specification.id}`} {...styles.printGroup}>
                        <Typography
                            as="h3"
                            variant="h3"
                            css={css`
                                page-break-inside: avoid;
                            `}
                        >
                            {specification.nameDisplay}
                        </Typography>
                        <span>{parse(transformContentHtml(htmlContent), parserOptions)}</span>
                    </StyledWrapper>
                );
            })}

            {documents && documents.length > 0 && (
                <StyledWrapper {...styles.printGroup}>
                    <Typography
                        as="h3"
                        variant="h3"
                        css={css`
                            page-break-inside: avoid;
                        `}
                    >
                        {translate("Resources")}
                    </Typography>
                    {documents!.map(document => (
                        <GridItem key={document.id.toString()} {...styles.documentGridItem}>
                            <Link
                                href={document.filePath}
                                target="_new"
                                {...styles.documentLink}
                                data-test-selector={`productDetails_${document.id}`}
                            >
                                <IconMemo {...styles.documentIcon} />
                                <Typography {...styles.documentNameText}>{document.name}</Typography>
                            </Link>
                        </GridItem>
                    ))}
                </StyledWrapper>
            )}
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(withProduct(ProductDetailsSpecifications)),
    definition: {
        displayName: "Specification",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
        fieldDefinitions: [
            {
                name: fields.showDocuments,
                displayName: "Show Documents",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.showAttributes,
                displayName: "Show Attributes",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 2,
            },
        ],
    },
};

export default widgetModule;
